/**
 * Visitor session persistence (concept §4).
 *
 * The joined visitor receives an opaque session credential which is stored
 * locally in that browser and lets the same browser reconnect during the active
 * period.
 *
 * Two rules the artifact fixes, both enforced here:
 *   - the credential is never placed in a shareable URL;
 *   - clearing local browser data may remove the ability to reconnect, and
 *     there is no account to fall back on. This module does not soften that.
 */

const STORAGE_KEY = 'tc.visitor-session';

export interface VisitorSession {
  conversationId: string;
  sessionToken: string;
  expiresAt: string;
}

export function saveVisitorSession(session: VisitorSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Private browsing or a blocked storage partition. The conversation still
    // works in this tab; reconnecting later will not.
  }
}

export function loadVisitorSession(conversationId?: string): VisitorSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as VisitorSession;
    if (conversationId && session.conversationId !== conversationId) return null;
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      clearVisitorSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function clearVisitorSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
}
